import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  TextField, 
  Typography, 
  Paper, 
  Avatar, 
  Alert,
  Grid,
  Tab,
  Tabs,
  CircularProgress,
  Link
} from '@mui/material';
import { 
  VerifiedUser as VerifiedUserIcon, 
  DirectionsBoat as BoatIcon,
  Login as LoginIcon,
  Backspace as BackspaceIcon 
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { isAuthSessionValid, updateAuthSession, getAuthSession, setLoggedOutState, clearLoggedOutState, clearAuthSession } from '../../utils/auth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
}

const LaunchRampAuthPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isJustLoggedOut, setIsJustLoggedOut] = useState(() => {
    const logoutFlag = sessionStorage.getItem('justLoggedOut');
    return logoutFlag === 'true';
  });
  
  const { 
    signIn, 
    verifyPasscode, 
    verifyPasscodeDirectly,
    userProfile, 
    isPasscodeAuthenticated,
    setUserRoleId
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const clearLoggedOutState = () => {
    console.log('Clearing logged out state');
    sessionStorage.removeItem('justLoggedOut');
    setIsJustLoggedOut(false);
  };
  
  // Handle forced redirects
  useEffect(() => {
    // Check URL params for forced=true which indicates a security redirect
    const searchParams = new URLSearchParams(window.location.search);
    const isForced = searchParams.get('forced') === 'true';
    
    if (isForced) {
      console.log('Detected forced security redirect to auth page - clearing any stale auth state');
      // Clear any possibly corrupted auth state
      clearAuthSession();
      // Force logout flag
      sessionStorage.setItem('justLoggedOut', 'true');
      setIsJustLoggedOut(true);
      // Show message to user
      setError('Authentication required. Please sign in to access this area.');
    }
  }, []);
  
  // Update to check for both location state and URL params for return path
  const returnTo = useMemo(() => {
    // First check if we have a returnTo in the URL parameters
    const searchParams = new URLSearchParams(window.location.search);
    const urlReturnPath = searchParams.get('returnTo') || searchParams.get('from');
    
    // Next check if we have a returnTo in the location state
    const locationState = location.state as { from?: string };
    const stateReturnPath = locationState?.from;
    
    // Also check sessionStorage for saved path
    const storedPath = sessionStorage.getItem('authRedirectPath');
    
    // Use the first available path, with priority to URL param
    const redirectPath = urlReturnPath || stateReturnPath || storedPath || '/staff/launch';
    
    // Store the path in sessionStorage for persistence
    sessionStorage.setItem('authRedirectPath', redirectPath);
    
    console.log('Authentication return path determined:', {
      urlReturnPath,
      stateReturnPath,
      storedPath,
      finalPath: redirectPath
    });
    
    return redirectPath;
  }, [location]);
  
  // Clear any stored redirect path when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('authRedirectPath');
    };
  }, []);
  
  // Check if this is a logout redirect or fix auth request
  useEffect(() => {
    if (location.state?.logout) {
      setError('');
      setSuccess('You have been logged out successfully.');
      // Set the logout flag in session storage
      sessionStorage.setItem('justLoggedOut', 'true');
      setIsJustLoggedOut(true);
    }
    
    if (location.state?.fix) {
      setError('Your session has expired. Please log in again.');
    }
  }, [location.state]);
  
  // Debug location state and returnTo
  useEffect(() => {
    console.log('LaunchRampAuthPage state:', {
      locationState: location.state,
      returnTo,
      userProfile: !!userProfile,
      isPasscodeAuthenticated: !!isPasscodeAuthenticated
    });
  }, [location.state, returnTo, userProfile, isPasscodeAuthenticated]);
  
  // If user is already logged in, move to passcode tab
  useEffect(() => {
    if (userProfile) {
      setTabValue(1); // Switch to passcode tab if logged in
    }
  }, [userProfile]);

  // If already authenticated, redirect to return URL
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      // Don't redirect if we just logged out or were forced here
      if (isJustLoggedOut) {
        console.log('Not redirecting - user just logged out or was forced here');
        return;
      }
      
      try {
        // Check if session is valid (await the Promise)
        const isValid = await isAuthSessionValid();
        console.log('Auth validation result:', isValid);
        
        if (isValid) {
          console.log('Auth session valid, redirecting to:', returnTo);
          navigate(returnTo, { replace: true });
        } else {
          console.log('Auth session invalid, staying on auth page');
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
      }
    };
    
    // Check immediately
    checkAuthAndRedirect();
    
    // Also listen for auth state changes
    const handleAuthChange = () => {
      checkAuthAndRedirect();
    };
    
    window.addEventListener('authStateChange', handleAuthChange);
    return () => {
      window.removeEventListener('authStateChange', handleAuthChange);
    };
  }, [navigate, returnTo, isJustLoggedOut]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Clear any logout flag when attempting to login
      clearLoggedOutState();
      
      await signIn(email, password);
      // Auth session will be set in AuthContext
      // Redirect will happen via the useEffect above
    } catch (error: any) {
      setError(error.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handlePasscodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passcode) {
      setError('Please enter your passcode');
      setSuccess('');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Clear any logout flag when attempting to enter passcode
      clearLoggedOutState();
      
      const isValid = await verifyPasscodeDirectly(passcode);
      
      if (isValid) {
        // Make sure the user role is properly set in auth session
        const userId = userProfile?.id || localStorage.getItem('currentUserId');
        if (userId && userProfile?.roleId) {
          // Set roleId and update auth session with correct role
          setUserRoleId(userProfile.roleId);
          updateAuthSession(true, userProfile.roleId);
          console.log('Updated auth session with roleId:', userProfile.roleId);
        } else {
          // Fallback to default true session if we don't have role info
          updateAuthSession(true);
          console.log('Updated auth session without roleId (missing profile info)');
        }
        
        // Get the return path from sessionStorage
        const returnPath = sessionStorage.getItem('authRedirectPath') || '/staff/launch';
        console.log('Redirecting after successful passcode auth to:', returnPath);
        navigate(returnPath);
      } else {
        setError('Invalid passcode. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify passcode');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Keypad handler functions
  const handleKeypadPress = (digit: string) => {
    setPasscode(prev => prev + digit);
  };

  const handleBackspace = () => {
    setPasscode(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPasscode('');
  };

  // Keypad component
  const NumericKeypad = () => (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Grid container spacing={1} justifyContent="center">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
          <Grid key={digit} item xs={4}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                height: 56, 
                fontSize: '1.25rem',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.5)'
                }
              }}
              onClick={() => handleKeypadPress(digit.toString())}
            >
              {digit}
            </Button>
          </Grid>
        ))}
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ 
              height: 56, 
              fontSize: '1.25rem',
              color: 'error.main',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                border: '1px solid rgba(211, 47, 47, 0.5)'
              }
            }}
            onClick={handleClear}
          >
            C
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ 
              height: 56, 
              fontSize: '1.25rem',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                border: '1px solid rgba(25, 118, 210, 0.5)'
              }
            }}
            onClick={() => handleKeypadPress('0')}
          >
            0
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ 
              height: 56, 
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                border: '1px solid rgba(25, 118, 210, 0.5)'
              }
            }}
            onClick={handleBackspace}
          >
            <BackspaceIcon />
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <BoatIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Staff Access
        </Typography>
        
        <Box sx={{ width: '100%', mt: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            aria-label="auth tabs"
          >
            <Tab 
              label="Login" 
              icon={<LoginIcon />} 
              iconPosition="start"
              {...a11yProps(0)} 
            />
            <Tab 
              label="Passcode" 
              icon={<VerifiedUserIcon />} 
              iconPosition="start"
              {...a11yProps(1)} 
            />
          </Tabs>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
              {success}
            </Alert>
          )}
          
          <TabPanel value={tabValue} index={0}>
            <Box component="form" onSubmit={handleLoginSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
              <Grid container justifyContent="center">
                <Grid item>
                  <Link href="/register" variant="body2">
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Box component="form" onSubmit={handlePasscodeSubmit} sx={{ width: '100%' }}>
              {!userProfile && (
                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                </Typography>
              )}
              
              <TextField
                required
                fullWidth
                name="passcode"
                label="Staff Passcode"
                type="password"
                id="passcode"
                autoComplete="current-passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ 
                  style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' },
                  maxLength: 8 
                }}
              />
              
              {/* Numeric Keypad */}
              <NumericKeypad />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
            </Box>
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default LaunchRampAuthPage; 